import React, { createContext, useContext, useMemo } from 'react';
import type { TenantConfig } from '@alterrego/core-config';
import { tokens, controlPatches, type ControlPatches } from './tokens';

/**
 * Élargit une échelle de tokens `as const` en types utilisables : un thème peut
 * poser n'importe quelle valeur sur une clé, pas seulement le littéral par défaut.
 */
type Scale<T> = { [K in keyof T]: T[K] extends number ? number : string };

type ColorMap = { [K in keyof typeof tokens.color]: string };
type FontMap = {
  family: string;
  size: Scale<typeof tokens.font.size>;
  weight: Scale<typeof tokens.font.weight>;
  lineHeight: Scale<typeof tokens.font.lineHeight>;
};

type Theme = {
  color: ColorMap;
  font: FontMap;
  radius: Scale<typeof tokens.radius>;
  spacing: Scale<typeof tokens.spacing>;
  shadow: Scale<typeof tokens.shadow>;
  control: ControlPatches;
  /** `null` en mode `theme={...}` : il n'y a pas de tenant derrière. */
  tenant: TenantConfig | null;
};

/**
 * Override de thème pour une app qui n'est pas pilotée par un `TenantConfig`.
 *
 * Les valeurs de couleur acceptent des références CSS (`'var(--color-cta-bg)'`) :
 * c'est le moyen de garder un thème clair/sombre piloté par l'app (attribut
 * `[data-theme]`, `prefers-color-scheme`…) tout en utilisant les composants
 * core-ui — la valeur est résolue au paint, pas au render React.
 *
 * En mode `theme`, le provider **n'écrit aucune variable CSS** sur
 * `documentElement` : l'app reste seule propriétaire de ses `--color-*`.
 */
export interface ThemeOverride {
  color?: Partial<ColorMap>;
  font?: {
    family?: string;
    size?: Partial<Scale<typeof tokens.font.size>>;
    weight?: Partial<Scale<typeof tokens.font.weight>>;
    lineHeight?: Partial<Scale<typeof tokens.font.lineHeight>>;
  };
  radius?: Partial<Scale<typeof tokens.radius>>;
  spacing?: Partial<Scale<typeof tokens.spacing>>;
  shadow?: Partial<Scale<typeof tokens.shadow>>;
  control?: DeepPartialControl;
}

type DeepPartialControl = {
  [K in keyof ControlPatches]?: { [P in keyof ControlPatches[K]]?: Partial<ControlPatches[K][P]> };
};

const ThemeContext = createContext<Theme | null>(null);

function mergeControl(base: ControlPatches, over?: DeepPartialControl): ControlPatches {
  if (!over) return base;
  const out = {} as ControlPatches;
  for (const group of Object.keys(base) as Array<keyof ControlPatches>) {
    const baseGroup = base[group] as unknown as Record<string, unknown>;
    const overGroup = (over[group] ?? {}) as Record<string, unknown>;
    const merged: Record<string, unknown> = {};
    for (const slot of Object.keys(baseGroup)) {
      merged[slot] = { ...(baseGroup[slot] as object), ...((overGroup[slot] ?? {}) as object) };
    }
    (out as unknown as Record<string, unknown>)[group] = merged;
  }
  return out;
}

export function ThemeProvider({
  config,
  theme: override,
  children,
}: {
  config?: TenantConfig;
  theme?: ThemeOverride;
  children: React.ReactNode;
}) {
  const theme = useMemo<Theme>(() => {
    // ── Mode override : l'app fournit son thème et garde ses propres CSS vars.
    if (!config) {
      if (!override) {
        throw new Error('<ThemeProvider> attend soit `config` (tenant) soit `theme` (override)');
      }
      return {
        ...tokens,
        color: { ...tokens.color, ...override.color },
        font: {
          ...tokens.font,
          family: override.font?.family ?? tokens.font.family,
          size: { ...tokens.font.size, ...override.font?.size },
          weight: { ...tokens.font.weight, ...override.font?.weight },
          lineHeight: { ...tokens.font.lineHeight, ...override.font?.lineHeight },
        },
        radius: { ...tokens.radius, ...override.radius },
        spacing: { ...tokens.spacing, ...override.spacing },
        shadow: { ...tokens.shadow, ...override.shadow },
        control: mergeControl(controlPatches, override.control),
        tenant: null,
      };
    }

    const tc = config.theme?.colors;
    const tf = config.theme?.typography;

    const color: ColorMap = {
      ...tokens.color,
      // branding (legacy — primary/secondary/dark)
      primary: config.branding.primary,
      primaryDark: config.branding.secondary,
      secondary: config.branding.secondary,
      ink: config.branding.dark,
      // theme.colors overrides (prend le dessus sur branding)
      ...(tc?.primary     && { primary:    tc.primary }),
      ...(tc?.secondary   && { secondary:  tc.secondary, primaryDark: tc.secondary }),
      ...(tc?.accent      && { accent:     tc.accent }),
      ...(tc?.surface     && { surface:    tc.surface, white: tc.surface }),
      ...(tc?.surfaceAlt  && { surfaceAlt: tc.surfaceAlt, grey50: tc.surfaceAlt }),
      ...(tc?.text        && { text:       tc.text, ink: tc.text }),
      ...(tc?.textMuted   && { textMuted:  tc.textMuted }),
      ...(tc?.border      && { border:     tc.border }),
      ...(tc?.success     && { success:    tc.success }),
      ...(tc?.warning     && { warning:    tc.warning }),
      ...(tc?.error       && { error:      tc.error, danger: tc.error }),
    };

    const fontFamily = tf?.fontFamily
      ?? `"${config.branding.fontFamily}", -apple-system, system-ui, sans-serif`;

    const merged: Theme = {
      ...tokens,
      color,
      font: { ...tokens.font, family: fontFamily },
      control: controlPatches,
      tenant: config,
    };

    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      // Colors
      root.style.setProperty('--color-primary',     color.primary);
      root.style.setProperty('--color-secondary',   color.secondary);
      root.style.setProperty('--color-accent',      color.accent);
      root.style.setProperty('--color-surface',     color.surface);
      root.style.setProperty('--color-surface-alt', color.surfaceAlt);
      root.style.setProperty('--color-text',        color.text);
      root.style.setProperty('--color-text-muted',  color.textMuted);
      root.style.setProperty('--color-border',      color.border);
      root.style.setProperty('--color-success',     color.success);
      root.style.setProperty('--color-warning',     color.warning);
      root.style.setProperty('--color-error',       color.error);
      root.style.setProperty('--color-info',        color.info);
      // Legacy vars (compat)
      root.style.setProperty('--color-primary-dark', color.primaryDark);
      root.style.setProperty('--color-ink',          color.ink);
      // Typography
      root.style.setProperty('--font-family', fontFamily);
      // Radius
      root.style.setProperty('--radius-sm',   tokens.radius.sm);
      root.style.setProperty('--radius-md',   tokens.radius.md);
      root.style.setProperty('--radius-lg',   tokens.radius.lg);
      root.style.setProperty('--radius-xl',   tokens.radius.xl);
      root.style.setProperty('--radius-full', tokens.radius.full);
      // Shadows
      root.style.setProperty('--shadow-sm', tokens.shadow.sm);
      root.style.setProperty('--shadow-md', tokens.shadow.md);
      root.style.setProperty('--shadow-lg', tokens.shadow.lg);
      // Spacing
      root.style.setProperty('--spacing-xs',  tokens.spacing.xs);
      root.style.setProperty('--spacing-sm',  tokens.spacing.sm);
      root.style.setProperty('--spacing-md',  tokens.spacing.md);
      root.style.setProperty('--spacing-lg',  tokens.spacing.lg);
      root.style.setProperty('--spacing-xl',  tokens.spacing.xl);
    }

    return merged;
  }, [config, override]);

  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
}

export function useTheme(): Theme {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme doit être utilisé dans un <ThemeProvider>');
  return ctx;
}
